/**
 * Mobile controls — a touch-region re-architecture, not a DOM append.
 *
 * ── What was wrong ────────────────────────────────────────────────────────
 *
 * v1 had three buttons (JUMP / SPRINT / LB), a joystick zone claiming the left
 * 50vw x 50vh, and this on the canvas (MobileControls.js:92):
 *
 *     if (touch.clientX > window.innerWidth * 0.5 && !touchCamera.active)
 *
 * ANY touch on the right half of the screen was swallowed as camera drag. There
 * was nowhere left to put four combat buttons: every one of them would have sat
 * inside the camera region and fought it for the same finger. Adding
 * basic + skill1 + skill2 + ultimate is therefore a redesign of who owns which
 * pixels, and that is what this file is.
 *
 * ── The new regions ───────────────────────────────────────────────────────
 *
 *   joystick zone  — bottom-left rect, an element that captures its own touches
 *   ability cluster— bottom-right rect, captures its WHOLE rect including the
 *                    gaps, so a thumb that misses a button by 6px does nothing
 *                    instead of aiming
 *   aim stick      — everything else, and it is defined by SUBTRACTION: the
 *                    canvas only ever sees touches that no control element
 *                    captured. There is no coordinate test anywhere below, which
 *                    is what makes the regions impossible to get out of sync
 *                    with the layout.
 *
 * Multi-touch falls out of this for free: three separate DOM subtrees, three
 * independent touch streams, so moving + aiming + casting at once works.
 *
 * ── Aim, not camera ───────────────────────────────────────────────────────
 *
 * The third region used to spin the camera. On the fixed-camera branch there is
 * no camera to spin, and that region became the thing the pivot was for: the
 * right thumb aims. A free-orbit camera needs three thumbs — move, look, fire —
 * and nobody has three.
 */

import { JOYSTICK_RADIUS, isTouch } from '../config.js';
import { press, release, setStick, setAimSlot } from './InputManager.js';
import { unlockAudio } from '../audio/SoundManager.js';

/**
 * Ability buttons, in thumb-reach order. Ids match the CSS.
 *
 * `aimed` is the Brawl Stars control, and it is the reason the pivot happened.
 * An aimed button does not fire when your thumb lands on it — it fires when
 * your thumb LEAVES it, in whatever direction you dragged. Press, drag to aim,
 * release to cast. On a phone that turns four buttons into four aimed weapons
 * without adding a single control, which is the whole trick.
 *
 * Dash and Jump are NOT aimed and fire on the press. Dash follows the movement
 * stick rather than the aim, and Jump has no direction at all, so deferring
 * either one to the release would buy nothing and cost the reaction time that
 * is the only reason you pressed it.
 */
const BUTTONS = [
  { id: 'tb-basic', action: 'basic', glyph: 'ATK', aimed: true },
  { id: 'tb-skill1', action: 'skill1', glyph: '1', aimed: true },
  { id: 'tb-skill2', action: 'skill2', glyph: '2', aimed: true },
  { id: 'tb-ultimate', action: 'ultimate', glyph: 'ULT', aimed: true },
  { id: 'tb-dash', action: 'dash', glyph: 'DASH', aimed: false },
  { id: 'tb-jump', action: 'jump', glyph: 'JUMP', aimed: false },
];

/**
 * Below this much drag the thumb has not committed to a direction yet.
 *
 * Shared by the free-area aim stick and by every aimed ability button, because
 * they are the same gesture asked for in two places and a player would feel two
 * different deadzones as one of them being broken.
 */
const AIM_DEADZONE_PX = 14;

/**
 * Reused. `setAimStick` stores the reference and reads `.x`/`.z` next frame, so
 * a fresh literal per `touchmove` is pure garbage — and two touch streams (the
 * free-area stick and an aimed ability button) can both be firing at up to
 * 120Hz on a phone, which is the worst platform to hand a GC.
 *
 * `null` still means released; only the non-null case shares this.
 */
const _aimVec = { x: 0, z: 0 };

function aimStickFrom(cameraController, dx, dz) {
  const mag = Math.hypot(dx, dz);
  if (mag < AIM_DEADZONE_PX) return false;
  _aimVec.x = dx / mag;
  _aimVec.z = dz / mag;
  cameraController?.setAimStick(_aimVec);
  return true;
}

const _buttonEls = new Map();

export function setupMobileControls({ rendererDomElement, cameraController }) {
  if (!isTouch) return null;
  document.body.classList.add('touch');

  const layer = document.getElementById('touch-layer');
  layer.innerHTML = `
    <div id="joystick-zone">
      <div id="joystick-visual" style="display:none">
        <div id="joystick-base"></div>
        <div id="joystick-thumb"></div>
      </div>
    </div>
    <div id="ability-cluster">
      ${BUTTONS.map(
        (b) =>
          `<div class="tbtn" id="${b.id}"><span class="glyph">${b.glyph}</span><span class="cd"></span><span class="cd-text"></span></div>`
      ).join('')}
    </div>
  `;

  setupJoystick(layer);
  setupAbilityButtons(layer, cameraController);
  setupAimStick(rendererDomElement, cameraController);

  // Landscape is the only orientation the cluster layout is designed for.
  if (screen.orientation?.lock) {
    void screen.orientation.lock('landscape').catch(() => {});
  }

  return { setCooldown, setUltCharged };
}

// ─── Joystick ───────────────────────────────────────────────────

function setupJoystick(layer) {
  const zone = layer.querySelector('#joystick-zone');
  const visual = layer.querySelector('#joystick-visual');
  const thumb = layer.querySelector('#joystick-thumb');

  let touchId = null;
  let originX = 0;
  let originY = 0;

  const zoneRect = () => zone.getBoundingClientRect();

  zone.addEventListener(
    'touchstart',
    (e) => {
      if (touchId !== null) return;
      unlockAudio();
      const touch = e.changedTouches[0];
      touchId = touch.identifier;
      originX = touch.clientX;
      originY = touch.clientY;

      const rect = zoneRect();
      visual.style.display = 'block';
      visual.style.left = `${touch.clientX - rect.left}px`;
      visual.style.top = `${touch.clientY - rect.top}px`;
      thumb.style.left = '0px';
      thumb.style.top = '0px';
      e.preventDefault();
    },
    { passive: false }
  );

  zone.addEventListener(
    'touchmove',
    (e) => {
      for (const touch of e.changedTouches) {
        if (touch.identifier !== touchId) continue;
        let dx = touch.clientX - originX;
        let dy = touch.clientY - originY;
        const dist = Math.hypot(dx, dy);
        if (dist > JOYSTICK_RADIUS) {
          dx = (dx / dist) * JOYSTICK_RADIUS;
          dy = (dy / dist) * JOYSTICK_RADIUS;
        }
        thumb.style.left = `${dx}px`;
        thumb.style.top = `${dy}px`;

        // WORLD space, like the keyboard's. Screen-up is -Z with the camera
        // fixed at yaw 0, and nothing rotates this any more — the sim used to
        // turn it by the player's facing, which is now aim rather than heading.
        // The sign flipped with that change: dragging up sends -Z directly.
        setStick({ x: dx / JOYSTICK_RADIUS, z: dy / JOYSTICK_RADIUS });
      }
      e.preventDefault();
    },
    { passive: false }
  );

  const end = (e) => {
    for (const touch of e.changedTouches) {
      if (touch.identifier !== touchId) continue;
      touchId = null;
      visual.style.display = 'none';
      setStick(null);
    }
  };
  zone.addEventListener('touchend', end, { passive: true });
  zone.addEventListener('touchcancel', end, { passive: true });
}

// ─── Ability cluster ────────────────────────────────────────────

function setupAbilityButtons(layer, cameraController) {
  const cluster = layer.querySelector('#ability-cluster');

  for (const spec of BUTTONS) {
    const el = layer.querySelector(`#${spec.id}`);
    _buttonEls.set(spec.action, el);

    /** Where this button's thumb went down, so aim can be relative to it. */
    let originX = 0;
    let originY = 0;
    let touchId = null;

    el.addEventListener(
      'touchstart',
      (e) => {
        unlockAudio();
        const touch = e.changedTouches[0];
        touchId = touch.identifier;
        originX = touch.clientX;
        originY = touch.clientY;
        el.classList.add('pressed');
        // Never latch. v1's SPRINT toggled, which is the parity break this
        // whole input layer exists to remove.
        //
        // An AIMED button holds its fire until the release — see BUTTONS. An
        // unaimed one goes now, because its whole value is that it went now.
        if (!spec.aimed) press(spec.action);
        // Claim the reticle from the moment the thumb lands, which on an aimed
        // button is well before the cast — the whole point is to see the reach
        // while you are still deciding where to point it.
        else setAimSlot(spec.action);
        e.preventDefault();
        e.stopPropagation();
      },
      { passive: false }
    );

    if (spec.aimed) {
      el.addEventListener(
        'touchmove',
        (e) => {
          for (const touch of e.changedTouches) {
            if (touch.identifier !== touchId) continue;
            // Under the deadzone the thumb has not chosen a direction, so this
            // is still a tap: leave the facing alone rather than snapping the
            // aim to whichever pixel it wobbled onto.
            aimStickFrom(cameraController, touch.clientX - originX, touch.clientY - originY);
          }
          e.preventDefault();
          e.stopPropagation();
        },
        { passive: false }
      );
    }

    const up = (e) => {
      touchId = null;
      el.classList.remove('pressed');
      if (spec.aimed) {
        // Fire on the RELEASE, in the direction the drag ended on. `press` then
        // `release` back to back is safe because InputManager latches a press
        // until the next tick consumes it — see its "Buffering" note. Without
        // that latch this would be a cast that never reached an input frame.
        press(spec.action);
        release(spec.action);
        setAimSlot(null);
        // Hand aim back to wherever it was. Leaving the stick set would freeze
        // the facing at the last drag until the next touch.
        cameraController?.setAimStick(null);
      } else {
        release(spec.action);
      }
      e.stopPropagation();
    };
    el.addEventListener('touchend', up, { passive: true });

    // A cancelled touch is the system taking the finger away — a notification,
    // a palm, a call. It must NOT fire: that is the one case where the player
    // did not choose to release.
    el.addEventListener(
      'touchcancel',
      (e) => {
        touchId = null;
        el.classList.remove('pressed');
        release(spec.action);
        setAimSlot(null);
        cameraController?.setAimStick(null);
        e.stopPropagation();
      },
      { passive: true }
    );
  }

  // The cluster's gaps swallow touches deliberately: a near-miss must not become
  // an aim swipe mid-cast.
  cluster.addEventListener('touchstart', (e) => e.preventDefault(), { passive: false });
}

/**
 * Cooldown ring on a touch button.
 *
 * @param {string} action
 * @param {number} remainingMs 0 when ready
 * @param {number} totalMs
 */
export function setCooldown(action, remainingMs, totalMs) {
  const el = _buttonEls.get(action);
  if (!el) return;
  const ready = remainingMs <= 0 || totalMs <= 0;
  el.classList.toggle('locked', !ready);
  el.style.setProperty('--cd-deg', ready ? '0deg' : `${(remainingMs / totalMs) * 360}deg`);
  const text = el.querySelector('.cd-text');
  if (text) text.textContent = ready ? '' : String(Math.ceil(remainingMs / 1000));
}

export function setUltCharged(charged) {
  const el = _buttonEls.get('ultimate');
  if (!el) return;
  el.classList.toggle('charged', charged);
  el.classList.toggle('locked', !charged);
}

// ─── Camera drag ────────────────────────────────────────────────

/**
 * Camera drag on the canvas.
 *
 * No coordinate test. The canvas is beneath the control elements, so it only
 * receives touches that nothing above it captured — the "region" is whatever is
 * left, and it can never drift out of sync with the CSS.
 */
/**
 * The right thumb AIMS. There is no camera to drag.
 *
 * This is the half of the twin-stick pivot that only exists on a phone, and it
 * is the reason for the pivot: a free-orbit camera needs a third thumb — one to
 * move, one to fire, one to look — and nobody has one. Brawl Stars reads well on
 * mobile because it spends its two thumbs on movement and aim and keeps the
 * camera fixed.
 *
 * A drag anywhere the controls do not already claim becomes an aim vector. It
 * is relative to where the thumb went down rather than absolute on screen, so
 * the aim does not jump the instant you touch, and it survives a thumb that
 * wanders — which on a 6-inch screen it always does.
 */
function setupAimStick(dom, cameraController) {
  let touchId = null;
  let originX = 0;
  let originY = 0;

  const clear = () => {
    touchId = null;
    cameraController?.setAimStick(null);
  };

  dom.addEventListener(
    'touchstart',
    (e) => {
      if (touchId !== null) return;
      unlockAudio();
      const touch = e.changedTouches[0];
      touchId = touch.identifier;
      originX = touch.clientX;
      originY = touch.clientY;
    },
    { passive: true }
  );

  dom.addEventListener(
    'touchmove',
    (e) => {
      for (const touch of e.changedTouches) {
        if (touch.identifier !== touchId) continue;
        // Screen DOWN is +Z on the ground with the camera fixed at yaw 0, so a
        // thumb dragged downward aims away from the viewer.
        //
        // Unlike an ability button, this one CLEARS below the deadzone: the
        // stick is the aim, so a thumb returning to where it started means
        // "stop aiming". A button's drag is a one-shot cast direction, and
        // wiping it mid-press would drop the aim the player already chose.
        const moved = aimStickFrom(cameraController, touch.clientX - originX, touch.clientY - originY);
        if (!moved) cameraController?.setAimStick(null);
      }
      e.preventDefault();
    },
    { passive: false }
  );

  dom.addEventListener('touchend', clear, { passive: true });
  dom.addEventListener('touchcancel', clear, { passive: true });
}

